import { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  opacity: number;
  fadeDirection: number;
  color: string;
}

interface Meteor {
  x: number;
  y: number;
  length: number;
  speed: number;
  opacity: number;
  angle: number;
  active: boolean;
  timer: number;
  interval: number;
}

interface Nebula {
  x: number;
  y: number;
  radius: number;
  opacity: number;
  pulseDir: number;
  color: string;
}

export default function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };
    resize();

    const W = canvas.width;
    const H = canvas.height;

    const starColors = [
      'rgba(212,175,55,',
      'rgba(200,182,255,',
      'rgba(255,255,255,',
      'rgba(255,220,120,',
    ];

    // 三层星星：小、中、大
    const stars: Star[] = [];
    const layers = [
      { count: 180, minR: 0.3, maxR: 0.8, speed: 0.15 },
      { count: 100, minR: 0.8, maxR: 1.5, speed: 0.25 },
      { count: 40,  minR: 1.5, maxR: 2.5, speed: 0.35 },
    ];
    layers.forEach(({ count, minR, maxR, speed }) => {
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          radius: minR + Math.random() * (maxR - minR),
          vx: (Math.random() - 0.5) * speed,
          vy: (Math.random() - 0.5) * speed,
          opacity: 0.2 + Math.random() * 0.8,
          fadeDirection: Math.random() > 0.5 ? 1 : -1,
          color: starColors[Math.floor(Math.random() * starColors.length)],
        });
      }
    });

    // 流星
    const meteors: Meteor[] = Array.from({ length: 6 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H * 0.5,
      length: 80 + Math.random() * 120,
      speed: 6 + Math.random() * 8,
      opacity: 0,
      angle: Math.PI / 5 + Math.random() * 0.3,
      active: false,
      timer: 0,
      interval: 120 + Math.floor(Math.random() * 300),
    }));

    // Hero 区域星云光晕（仅在页面顶部 100vh 范围内）
    const heroH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const nebulae: Nebula[] = [
      { x: W * 0.15, y: heroH * 0.3, radius: 220, opacity: 0.06, pulseDir: 1, color: '212,175,55' },
      { x: W * 0.85, y: heroH * 0.5, radius: 180, opacity: 0.05, pulseDir: -1, color: '200,182,255' },
      { x: W * 0.5,  y: heroH * 0.8, radius: 260, opacity: 0.04, pulseDir: 1, color: '180,120,255' },
    ];

    let frame = 0;
    let animId: number;

    const drawStar = (star: Star) => {
      // 光晕
      const glow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.radius * 4);
      glow.addColorStop(0, `${star.color}${(star.opacity * 0.4).toFixed(2)})`);
      glow.addColorStop(1, `${star.color}0)`);
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius * 4, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();
      // 核心
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
      ctx.fillStyle = `${star.color}${star.opacity.toFixed(2)})`;
      ctx.fill();
    };

    const drawMeteor = (m: Meteor) => {
      if (!m.active) return;
      const dx = Math.cos(m.angle) * m.length;
      const dy = Math.sin(m.angle) * m.length;
      const grad = ctx.createLinearGradient(m.x, m.y, m.x - dx, m.y - dy);
      grad.addColorStop(0, `rgba(255,255,220,${m.opacity})`);
      grad.addColorStop(0.3, `rgba(212,175,55,${m.opacity * 0.6})`);
      grad.addColorStop(1, 'rgba(212,175,55,0)');
      ctx.beginPath();
      ctx.moveTo(m.x, m.y);
      ctx.lineTo(m.x - dx, m.y - dy);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    };

    const drawNebula = (n: Nebula) => {
      const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius);
      grad.addColorStop(0, `rgba(${n.color},${n.opacity})`);
      grad.addColorStop(0.5, `rgba(${n.color},${(n.opacity * 0.4).toFixed(3)})`);
      grad.addColorStop(1, `rgba(${n.color},0)`);
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      // 星云脉冲
      nebulae.forEach((n) => {
        n.opacity += n.pulseDir * 0.0003;
        if (n.opacity > 0.09) n.pulseDir = -1;
        if (n.opacity < 0.02) n.pulseDir = 1;
        drawNebula(n);
      });

      // 星星
      stars.forEach((star) => {
        star.x += star.vx;
        star.y += star.vy;
        star.opacity += star.fadeDirection * 0.008;
        if (star.opacity >= 1) star.fadeDirection = -1;
        else if (star.opacity <= 0.1) star.fadeDirection = 1;
        if (star.x < 0) star.x = canvas.width;
        if (star.x > canvas.width) star.x = 0;
        if (star.y < 0) star.y = canvas.height;
        if (star.y > canvas.height) star.y = 0;
        drawStar(star);
      });

      // 流星
      meteors.forEach((m) => {
        m.timer++;
        if (!m.active) {
          if (m.timer >= m.interval) {
            m.active = true;
            m.timer = 0;
            m.x = Math.random() * canvas.width * 0.8;
            m.y = Math.random() * heroH * 0.4;
            m.opacity = 0;
          }
        } else {
          if (m.opacity < 0.9 && m.timer < 8) m.opacity += 0.12;
          m.x += Math.cos(m.angle) * m.speed;
          m.y += Math.sin(m.angle) * m.speed;
          if (m.timer > 20) m.opacity -= 0.06;
          if (m.opacity <= 0) {
            m.active = false;
            m.timer = 0;
            m.interval = 150 + Math.floor(Math.random() * 350);
          }
          drawMeteor(m);
        }
      });

      animId = requestAnimationFrame(animate);
    };

    animate();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
    />
  );
}
